
// Shared address type-ahead (#689). Talks to Flow's two Oracle proxies, owns the
// Google billing session token, and emits ONE normalized address on a pick:
//   select → { street_1, street_2, city, state, zip_code, formatted, complete }
// The host decides what to do with each field (and pushes the ZIP through its
// own ZIP path so county/fips derive exactly as when the ZIP is typed). The
// manual fields underneath are never hidden by this component.
//
// The root is a plain div with the <input> as a direct child on purpose: the
// host's field wrapper (.sv2-field / .ck-field / .sadm-field / bootstrap via
// inputClass) styles the input; only the dropdown is styled here (overrides.scss).
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import { isUnavailable, markUnavailable } from '~/util/address-search-state'

let instances = 0

export default {
    props: {
        id: { type: String, default: null },
        label: { type: [String, Boolean], default: 'Find your address' },
        hint: { type: [String, Boolean], default: 'Pick a match and we’ll fill in the fields below, or type them yourself.' },
        placeholder: { type: String, default: 'Start typing your street address' },
        disabled: { type: Boolean, default: false },
        inputClass: { type: String, default: '' },
        minChars: { type: Number, default: 3 },
        debounceMs: { type: Number, default: 250 },
    },
    data () {
        instances += 1
        return {
            uid: instances,
            q: '',
            items: [],
            open: false,
            loading: false,
            activeIndex: -1,
            sessionToken: null,
            seq: 0,
            hidden: isUnavailable(),
            blurTimer: null,
        }
    },
    computed: {
        inputId () { return this.id || `addr-search-${this.uid}` },
        listId () { return `${this.inputId}-list` },
    },
    created () {
        // Per-instance debounce — an options-level _.debounce would be shared
        // across every mounted AddressSearch (permanent + mailing on one page).
        this.debouncedSearch = _.debounce(() => this.search(), this.debounceMs)
    },
    beforeDestroy () {
        this.debouncedSearch.cancel()
        clearTimeout(this.blurTimer)
    },
    methods: {
        optionId (i) { return `${this.listId}-opt-${i}` },
        onInput (v) {
            this.q = v
            this.activeIndex = -1
            if (!v.trim()) {
                // Cleared: the billing session ends with nothing picked.
                this.sessionToken = null
                this.items = []
                this.open = false
                this.debouncedSearch.cancel()
                return
            }
            this.open = true
            this.debouncedSearch()
        },
        onFocus () {
            if (this.items.length) this.open = true
        },
        onBlur () {
            // Delay so a mousedown on a row (which prevents default, so focus
            // stays) is never raced by the blur closing the list.
            clearTimeout(this.blurTimer)
            this.blurTimer = setTimeout(() => { this.open = false }, 150)
        },
        close () {
            this.open = false
            this.activeIndex = -1
        },
        move (delta) {
            if (!this.items.length) return
            this.open = true
            const n = this.items.length
            this.activeIndex = (this.activeIndex + delta + n) % n
        },
        pickActive () {
            if (this.open && this.activeIndex >= 0 && this.items[this.activeIndex]) {
                this.pick(this.items[this.activeIndex])
            }
        },
        async search () {
            const input = this.q.trim()
            if (this.hidden || input.length < this.minChars) return
            if (!this.sessionToken) this.sessionToken = uuidv4()
            const seq = ++this.seq
            this.loading = true
            try {
                const resp = await this.$api.addressAutocomplete(input, this.sessionToken)
                if (seq !== this.seq) return // a newer keystroke owns the list now
                this.items = (resp && resp.suggestions) || []
                this.activeIndex = -1
            } catch (e) {
                if (seq !== this.seq) return
                this.items = []
                this.handleError(e)
            } finally {
                if (seq === this.seq) this.loading = false
            }
        },
        async pick (s) {
            const token = this.sessionToken || uuidv4()
            this.q = s.text
            this.items = []
            this.open = false
            this.activeIndex = -1
            this.seq++ // drop any autocomplete still in flight
            this.debouncedSearch.cancel()
            try {
                const address = await this.$api.addressDetails(s.place_id, token)
                if (address && address.formatted) this.q = address.formatted
                this.$emit('select', address)
            } catch (e) {
                this.handleError(e)
            } finally {
                // The details call ends the Google session; the next keystroke
                // starts a fresh one.
                this.sessionToken = null
            }
        },
        handleError (e) {
            const status = e && e.response && e.response.status
            if (status === 503) {
                // Oracle/Google is down for everyone: hide, page-wide, for the
                // session. Manual entry underneath is unaffected.
                markUnavailable()
                this.hidden = true
                this.$emit('unavailable')
            }
            // 429 (rate limit) and anything else: no suggestions this keystroke.
        },
    },
}
